import React, { useState, useEffect } from 'react';
import api from '../services/api';
import documentosLegalesService from '../services/documentosLegalesService';
import { toast } from 'react-toastify';

import Loading from './Loading';
const ALLOWED_TRANSITIONS = {
  borrador: ['emitida', 'anulada'],
  emitida: ['pagada', 'vencida', 'anulada'],
  pagada: [],
  vencida: ['pagada', 'anulada'],
  anulada: [],
};

const ESTADO_LABELS = {
  borrador: 'Borrador',
  emitida: 'Emitida',
  pagada: 'Pagada',
  vencida: 'Vencida',
  anulada: 'Anulada',
};

const ESTADO_COLORS = {
  borrador: '#6b7280',
  emitida: '#3b82f6',
  pagada: '#10b981',
  vencida: '#f59e0b',
  anulada: '#ef4444',
};

const getContabilidadApiUrl = () => {
  const baseURL = api?.defaults?.baseURL || '/api/content';
  return baseURL.replace(/\/content\/?$/i, '/contabilidad');
};

const DocumentoLegalDetailPanel = ({
  documentoId,
  onClose,
  onEdit,
  onDelete,
  onChangeEstado,
  onNuevoPago,
}) => {
  const [documento, setDocumento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contabilizando, setContabilizando] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [voucherData, setVoucherData] = useState(null);
  const [loadingVoucher, setLoadingVoucher] = useState(false);

  // Voucher edit state
  const [voucherEditMode, setVoucherEditMode] = useState(false);
  const [voucherEditForm, setVoucherEditForm] = useState(null);
  const [savingVoucher, setSavingVoucher] = useState(false);
  const [planCuentas, setPlanCuentas] = useState([]);
  const [planCuentasLoaded, setPlanCuentasLoaded] = useState(false);

  useEffect(() => {
    if (!documentoId) return;
    const cargarDetalle = async () => {
      try {
        setLoading(true);
        const baseURL = api?.defaults?.baseURL || '/api/content';
        const API_BASE_URL = baseURL.replace(/\/content\/?$/i, '/contabilidad');
        const response = await api.get(`${API_BASE_URL}/documentos-legales/${documentoId}/`);
        setDocumento(response.data);
      } catch (error) {
        console.error('Error al cargar detalle:', error);
        toast.error('Error al cargar el detalle del documento');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    cargarDetalle();
  }, [documentoId]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value || 0);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-CL');
  };

  const formatDateConDia = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString + 'T00:00:00');
    const dias = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
    return `${date.toLocaleDateString('es-CL')} (${dias[date.getDay()]})`;
  };

  const getEstadoBadgeClass = (estado) => {
    const map = {
      borrador: 'badge-borrador',
      emitida: 'badge-emitida',
      pagada: 'badge-pagada',
      vencida: 'badge-vencida',
      anulada: 'badge-anulada',
    };
    return map[estado] || 'badge-default';
  };

  const getTipoBadgeClass = (tipo) => {
    const map = {
      factura_compra: 'badge-factura-compra',
      factura_venta: 'badge-factura-venta',
      boleta: 'badge-boleta',
      nota_credito: 'badge-nota-credito',
      nota_debito: 'badge-nota-debito',
    };
    return map[tipo] || 'badge-default';
  };

  const transitions = documento ? (ALLOWED_TRANSITIONS[documento.estado] || []) : [];

  const handleContabilizar = async () => {
    if (!documento) return;
    setContabilizando(true);
    try {
      const response = await documentosLegalesService.contabilizar(documento.id);
      setDocumento(response.data.documento);
      toast.success(response.data.message || 'Documento contabilizado exitosamente');
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al contabilizar el documento';
      toast.error(msg);
    } finally {
      setContabilizando(false);
    }
  };

  // --- Voucher Edit Functions ---
  const cargarPlanCuentas = async () => {
    if (planCuentasLoaded) return;
    try {
      const url = `${getContabilidadApiUrl()}/plan-cuentas/?imputa=true&activo=true&page_size=500`;
      const res = await api.get(url);
      setPlanCuentas(res.data.results || res.data || []);
      setPlanCuentasLoaded(true);
    } catch {
      toast.error('Error al cargar plan de cuentas');
    }
  };

  const handleEnterVoucherEdit = async () => {
    await cargarPlanCuentas();
    setVoucherEditForm({
      fecha: voucherData.fecha || '',
      glosa: voucherData.glosa || '',
      estado: voucherData.estado || 'borrador',
      detalles: (voucherData.detalles || []).map(det => ({
        plan_cuenta: det.plan_cuenta,
        plan_cuenta_codigo: det.plan_cuenta_codigo || '',
        plan_cuenta_nombre: det.plan_cuenta_nombre || '',
        tipo_movimiento: det.tipo_movimiento,
        monto: det.monto || 0,
        glosa: det.glosa || '',
      })),
    });
    setVoucherEditMode(true);
  };

  const handleCancelVoucherEdit = () => {
    setVoucherEditMode(false);
    setVoucherEditForm(null);
  };

  const handleVoucherFieldChange = (field, value) => {
    setVoucherEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleDetalleChange = (index, field, value) => {
    setVoucherEditForm(prev => {
      const detalles = [...prev.detalles];
      detalles[index] = { ...detalles[index], [field]: value };
      if (field === 'plan_cuenta') {
        const cuenta = planCuentas.find(c => String(c.id) === String(value));
        if (cuenta) {
          detalles[index].plan_cuenta_codigo = cuenta.codigo_completo || cuenta.codigo;
          detalles[index].plan_cuenta_nombre = cuenta.nombre;
        }
      }
      return { ...prev, detalles };
    });
  };

  const handleAddDetalle = () => {
    setVoucherEditForm(prev => ({
      ...prev,
      detalles: [...prev.detalles, { plan_cuenta: '', tipo_movimiento: 'debe', monto: 0, glosa: '' }],
    }));
  };

  const handleRemoveDetalle = (index) => {
    setVoucherEditForm(prev => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index),
    }));
  };

  const calcEditTotals = () => {
    if (!voucherEditForm) return { debe: 0, haber: 0 };
    const debe = voucherEditForm.detalles.filter(d => d.tipo_movimiento === 'debe').reduce((s, d) => s + (parseFloat(d.monto) || 0), 0);
    const haber = voucherEditForm.detalles.filter(d => d.tipo_movimiento === 'haber').reduce((s, d) => s + (parseFloat(d.monto) || 0), 0);
    return { debe, haber };
  };

  const handleSaveVoucher = async () => {
    if (!voucherEditForm || !voucherData) return;
    const { debe, haber } = calcEditTotals();
    if (voucherEditForm.estado === 'aprobado' && debe !== haber) {
      toast.error('El asiento debe estar balanceado para aprobarlo');
      return;
    }
    if (voucherEditForm.detalles.some(d => !d.plan_cuenta)) {
      toast.error('Todas las lineas deben tener una cuenta asignada');
      return;
    }
    try {
      setSavingVoucher(true);
      const payload = {
        fecha: voucherEditForm.fecha,
        glosa: voucherEditForm.glosa,
        estado: voucherEditForm.estado,
        detalles: voucherEditForm.detalles.map(d => ({
          plan_cuenta: d.plan_cuenta,
          tipo_movimiento: d.tipo_movimiento,
          monto: parseFloat(d.monto) || 0,
          glosa: d.glosa || '',
        })),
      };
      const url = `${getContabilidadApiUrl()}/asientos-contables/${voucherData.id}/`;
      const res = await api.put(url, payload);
      // Reload full voucher data
      const fullRes = await api.get(url);
      setVoucherData(fullRes.data);
      setVoucherEditMode(false);
      setVoucherEditForm(null);
      toast.success('Voucher actualizado exitosamente');
      // Refresh documento to update asiento_info
      try {
        const docRes = await api.get(`${getContabilidadApiUrl()}/documentos-legales/${documentoId}/`);
        setDocumento(docRes.data);
      } catch { /* ignore */ }
    } catch (error) {
      const errData = error.response?.data;
      if (errData && typeof errData === 'object') {
        const msgs = Object.entries(errData).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        toast.error(msgs);
      } else {
        toast.error('Error al guardar el voucher');
      }
    } finally {
      setSavingVoucher(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="docleg-detail-overlay" onClick={onClose} />

      {/* Voucher Modal */}
      {showVoucherModal && voucherData && (
        <>
          <div className="voucher-modal-overlay" onClick={() => { setShowVoucherModal(false); handleCancelVoucherEdit(); }} />
          <div className="voucher-modal">
            <div className="voucher-modal-header">
              <h3>{voucherEditMode ? 'Editar' : ''} Voucher N° {voucherData.numero_asiento}</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {!voucherEditMode && (
                  <button
                    className="docleg-action-btn docleg-action-edit"
                    onClick={handleEnterVoucherEdit}
                  >
                    Editar
                  </button>
                )}
                <button className="btn-icon" onClick={() => { setShowVoucherModal(false); handleCancelVoucherEdit(); }} title="Cerrar">&times;</button>
              </div>
            </div>
            <div className="voucher-modal-body">
              {/* --- VIEW MODE --- */}
              {!voucherEditMode && (
                <>
                  <div className="voucher-info-grid">
                    <div className="voucher-info-field">
                      <span className="voucher-info-label">Fecha</span>
                      <span className="voucher-info-value">{voucherData.fecha ? new Date(voucherData.fecha).toLocaleDateString('es-CL') : '-'}</span>
                    </div>
                    <div className="voucher-info-field">
                      <span className="voucher-info-label">Estado</span>
                      <span className={`badge ${voucherData.estado === 'aprobado' ? 'badge-aprobado' : 'badge-borrador'}`}>
                        {voucherData.estado_display || voucherData.estado}
                      </span>
                    </div>
                    <div className="voucher-info-field">
                      <span className="voucher-info-label">Documento</span>
                      <span className="voucher-info-value">{voucherData.documento_legal_numero || '-'}</span>
                    </div>
                    <div className="voucher-info-field">
                      <span className="voucher-info-label">Tipo</span>
                      <span className="voucher-info-value">{voucherData.documento_legal_tipo || '-'}</span>
                    </div>
                  </div>
                  {voucherData.glosa && (
                    <div className="voucher-glosa">
                      <span className="voucher-info-label">Glosa</span>
                      <p>{voucherData.glosa}</p>
                    </div>
                  )}
                  {voucherData.detalles && voucherData.detalles.length > 0 && (
                    <div className="voucher-detalles">
                      <h4>Detalle del Asiento</h4>
                      <table className="voucher-detalles-table">
                        <thead>
                          <tr>
                            <th>Cuenta</th>
                            <th>Glosa</th>
                            <th style={{ textAlign: 'right' }}>Debe</th>
                            <th style={{ textAlign: 'right' }}>Haber</th>
                          </tr>
                        </thead>
                        <tbody>
                          {voucherData.detalles.map((det, i) => (
                            <tr key={det.id || i}>
                              <td>
                                <span className="voucher-cuenta-codigo">{det.plan_cuenta_codigo}</span>
                                <span className="voucher-cuenta-nombre">{det.plan_cuenta_nombre}</span>
                              </td>
                              <td>{det.glosa || '-'}</td>
                              <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>
                                {det.tipo_movimiento === 'debe' ? formatCurrency(det.monto) : '-'}
                              </td>
                              <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>
                                {det.tipo_movimiento === 'haber' ? formatCurrency(det.monto) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="voucher-totales-row">
                            <td colSpan="2"><strong>Totales</strong></td>
                            <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>
                              <strong>{formatCurrency(voucherData.monto_debe)}</strong>
                            </td>
                            <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>
                              <strong>{formatCurrency(voucherData.monto_haber)}</strong>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                      <div className="voucher-balance-indicator" style={{ color: voucherData.balanceado ? '#059669' : '#dc2626' }}>
                        {voucherData.balanceado ? 'Asiento Balanceado' : 'Asiento Desbalanceado'}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* --- EDIT MODE --- */}
              {voucherEditMode && voucherEditForm && (() => {
                const totals = calcEditTotals();
                const balanced = totals.debe === totals.haber;
                return (
                  <>
                    <div className="voucher-edit-grid">
                      <div className="voucher-edit-field">
                        <label>Fecha</label>
                        <input
                          type="date"
                          value={voucherEditForm.fecha}
                          onChange={e => handleVoucherFieldChange('fecha', e.target.value)}
                        />
                      </div>
                      <div className="voucher-edit-field">
                        <label>Estado</label>
                        <select
                          value={voucherEditForm.estado}
                          onChange={e => handleVoucherFieldChange('estado', e.target.value)}
                        >
                          <option value="borrador">Borrador</option>
                          <option value="aprobado">Aprobado</option>
                          <option value="anulado">Anulado</option>
                        </select>
                      </div>
                      <div className="voucher-edit-field" style={{ gridColumn: 'span 2' }}>
                        <label>Glosa</label>
                        <textarea
                          value={voucherEditForm.glosa}
                          onChange={e => handleVoucherFieldChange('glosa', e.target.value)}
                          rows={2}
                        />
                      </div>
                    </div>

                    <div className="voucher-detalles" style={{ marginTop: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <h4 style={{ margin: 0 }}>Detalle del Asiento</h4>
                        <button
                          className="docleg-action-btn docleg-action-edit"
                          onClick={handleAddDetalle}
                          style={{ fontSize: '0.75rem' }}
                        >
                          + Agregar Linea
                        </button>
                      </div>
                      <table className="voucher-detalles-table voucher-edit-table">
                        <thead>
                          <tr>
                            <th>Cuenta</th>
                            <th>Tipo</th>
                            <th>Monto</th>
                            <th>Glosa</th>
                            <th style={{ width: 40 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {voucherEditForm.detalles.map((det, i) => (
                            <tr key={i}>
                              <td>
                                <select
                                  value={det.plan_cuenta || ''}
                                  onChange={e => handleDetalleChange(i, 'plan_cuenta', e.target.value)}
                                  className="voucher-edit-select"
                                >
                                  <option value="">Seleccionar...</option>
                                  {planCuentas.map(c => (
                                    <option key={c.id} value={c.id}>
                                      {c.codigo_completo || c.codigo} - {c.nombre}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <select
                                  value={det.tipo_movimiento}
                                  onChange={e => handleDetalleChange(i, 'tipo_movimiento', e.target.value)}
                                  className="voucher-edit-select-sm"
                                >
                                  <option value="debe">Debe</option>
                                  <option value="haber">Haber</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="number"
                                  value={det.monto}
                                  onChange={e => handleDetalleChange(i, 'monto', e.target.value)}
                                  className="voucher-edit-input-num"
                                  min="0"
                                  step="1"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={det.glosa}
                                  onChange={e => handleDetalleChange(i, 'glosa', e.target.value)}
                                  className="voucher-edit-input"
                                  placeholder="Glosa..."
                                />
                              </td>
                              <td>
                                {voucherEditForm.detalles.length > 1 && (
                                  <button
                                    className="btn-icon btn-danger"
                                    onClick={() => handleRemoveDetalle(i)}
                                    title="Eliminar linea"
                                    style={{ fontSize: '1rem', padding: '0.25rem' }}
                                  >
                                    &times;
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="voucher-totales-row">
                            <td colSpan="2"><strong>Totales</strong></td>
                            <td colSpan="2" style={{ fontFamily: "'Courier New', monospace", fontSize: '0.8125rem' }}>
                              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'space-between' }}>
                                <span>Debe: <strong>{formatCurrency(totals.debe)}</strong></span>
                                <span>Haber: <strong>{formatCurrency(totals.haber)}</strong></span>
                              </div>
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                      <div className="voucher-balance-indicator" style={{ color: balanced ? '#059669' : '#dc2626' }}>
                        {balanced ? 'Asiento Balanceado' : `Desbalanceado (Dif: ${formatCurrency(Math.abs(totals.debe - totals.haber))})`}
                      </div>
                    </div>

                    <div className="voucher-edit-actions">
                      <button className="btn-cancelar-voucher" onClick={handleCancelVoucherEdit}>
                        Cancelar
                      </button>
                      <button
                        className="btn-guardar-voucher"
                        onClick={handleSaveVoucher}
                        disabled={savingVoucher}
                      >
                        {savingVoucher ? 'Guardando...' : 'Guardar Voucher'}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Panel */}
      <div className="docleg-detail-panel">
        {/* Header */}
        <div className="docleg-detail-header">
          <div style={{ flex: 1 }}>
            {loading ? (
              <Loading inline size="sm" />
            ) : documento ? (
              <>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', color: '#1f2937' }}>
                  {documento.numero_documento}
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <span className={`badge ${getTipoBadgeClass(documento.tipo_documento)}`}>
                    {documento.tipo_documento_display}
                  </span>
                  <span className={`badge ${getEstadoBadgeClass(documento.estado)}`}>
                    {documento.estado_display}
                  </span>
                </div>
              </>
            ) : null}
          </div>
          <button
            className="btn-icon"
            onClick={onClose}
            style={{ fontSize: '1.25rem' }}
            title="Cerrar"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <Loading fullScreen={false} size="sm" mensaje="Cargando detalles" />
        ) : documento ? (
          <div className="docleg-detail-content">
            {/* Info Section */}
            <div className="docleg-detail-section">
              <h4>Informacion General</h4>
              <div className="docleg-detail-grid">
                <div className="docleg-detail-field">
                  <span className="docleg-detail-label">Fecha Emision</span>
                  <span className="docleg-detail-value">{formatDate(documento.fecha_emision)}</span>
                </div>
                <div className="docleg-detail-field">
                  <span className="docleg-detail-label">Fecha Vencimiento</span>
                  <span className="docleg-detail-value">{formatDate(documento.fecha_vencimiento)}</span>
                </div>
                {documento.fecha_pago_programada && (
                  <div className="docleg-detail-field">
                    <span className="docleg-detail-label">Fecha Pago Programada</span>
                    <span className="docleg-detail-value" style={{ color: '#7c3aed', fontWeight: 600 }}>
                      {formatDateConDia(documento.fecha_pago_programada)}
                    </span>
                  </div>
                )}
                {documento.proveedor_nombre && (
                  <div className="docleg-detail-field">
                    <span className="docleg-detail-label">Proveedor</span>
                    <span className="docleg-detail-value">{documento.proveedor_nombre}</span>
                  </div>
                )}
                {documento.cliente_nombre && (
                  <div className="docleg-detail-field">
                    <span className="docleg-detail-label">Cliente</span>
                    <span className="docleg-detail-value">{documento.cliente_nombre}</span>
                  </div>
                )}
                {documento.proyecto_nombre && (
                  <div className="docleg-detail-field">
                    <span className="docleg-detail-label">Proyecto</span>
                    <span className="docleg-detail-value">{documento.proyecto_nombre}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Items/Details Section */}
            {documento.detalles && documento.detalles.length > 0 && (
              <div className="docleg-detail-section">
                <h4>Detalle de Items</h4>
                <table className="docleg-detail-items-table">
                  <thead>
                    <tr>
                      <th>Descripcion</th>
                      <th style={{ textAlign: 'right' }}>Cant.</th>
                      <th style={{ textAlign: 'right' }}>P. Unit.</th>
                      <th style={{ textAlign: 'right' }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documento.detalles.map((det, i) => (
                      <tr key={i}>
                        <td>{det.descripcion}</td>
                        <td style={{ textAlign: 'right' }}>{det.cantidad}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(det.precio_unitario)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(det.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals Section */}
            <div className="docleg-detail-section">
              <h4>Montos</h4>
              <div className="docleg-detail-totals">
                <div className="docleg-detail-total-row">
                  <span>Monto Neto</span>
                  <span>{formatCurrency(documento.monto_neto)}</span>
                </div>
                <div className="docleg-detail-total-row">
                  <span>IVA</span>
                  <span>{formatCurrency(documento.monto_iva)}</span>
                </div>
                <div className="docleg-detail-total-row docleg-detail-total-final">
                  <span>Total</span>
                  <span>{formatCurrency(documento.monto_total)}</span>
                </div>
              </div>
            </div>

            {/* Asiento Contable Section */}
            {documento.contabilizado && documento.asiento_info && (
              <div className="docleg-detail-section">
                <h4>Asiento Contable</h4>
                <div className="docleg-asiento-info">
                  <div className="docleg-detail-grid">
                    <div className="docleg-detail-field">
                      <span className="docleg-detail-label">N Asiento</span>
                      <span className="docleg-detail-value">{documento.asiento_info.numero_asiento}</span>
                    </div>
                    <div className="docleg-detail-field">
                      <span className="docleg-detail-label">Estado</span>
                      <span className={`badge ${documento.asiento_info.estado === 'aprobado' ? 'badge-aprobado' : 'badge-borrador'}`}>
                        {documento.asiento_info.estado === 'aprobado' ? 'Aprobado' : 'Borrador'}
                      </span>
                    </div>
                    <div className="docleg-detail-field">
                      <span className="docleg-detail-label">Debe</span>
                      <span className="docleg-detail-value">{formatCurrency(documento.asiento_info.monto_debe)}</span>
                    </div>
                    <div className="docleg-detail-field">
                      <span className="docleg-detail-label">Haber</span>
                      <span className="docleg-detail-value">{formatCurrency(documento.asiento_info.monto_haber)}</span>
                    </div>
                  </div>
                  <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.8125rem', color: documento.asiento_info.balanceado ? '#059669' : '#dc2626' }}>
                      {documento.asiento_info.balanceado ? 'Balanceado' : 'Desbalanceado'}
                    </span>
                    <button
                      className="btn-primary"
                      style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}
                      disabled={loadingVoucher}
                      onClick={async () => {
                        try {
                          setLoadingVoucher(true);
                          const url = `${getContabilidadApiUrl()}/asientos-contables/${documento.asiento_info.id}/`;
                          const res = await api.get(url);
                          setVoucherData(res.data);
                          setShowVoucherModal(true);
                        } catch {
                          toast.error('Error al cargar el voucher');
                        } finally {
                          setLoadingVoucher(false);
                        }
                      }}
                    >
                      {loadingVoucher ? 'Cargando...' : 'Ver Voucher'}
                    </button>
                    {onNuevoPago && ['emitida', 'vencida'].includes(documento.estado) && (
                      <button
                        className="btn-primary"
                        style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem', background: '#059669' }}
                        onClick={() => onNuevoPago(documento)}
                      >
                        + Nuevo Pago
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contabilizar Button */}
            {!documento.contabilizado && ['emitida', 'pagada'].includes(documento.estado) && (
              <div className="docleg-detail-section">
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn-contabilizar"
                    onClick={handleContabilizar}
                    disabled={contabilizando}
                  >
                    {contabilizando ? 'Contabilizando...' : 'Contabilizar Documento'}
                  </button>
                  {onNuevoPago && ['emitida', 'vencida'].includes(documento.estado) && (
                    <button
                      className="btn-primary"
                      style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem', background: '#059669' }}
                      onClick={() => onNuevoPago(documento)}
                    >
                      + Nuevo Pago
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Attachment */}
            {documento.documento_adjunto && (
              <div className="docleg-detail-section">
                <h4>Adjunto</h4>
                <a
                  href={documento.documento_adjunto}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  Descargar Adjunto
                </a>
              </div>
            )}

            {/* Actions */}
            <div className="docleg-detail-section">
              <div className="docleg-detail-actions">
                <button
                  className="docleg-action-btn docleg-action-edit"
                  onClick={() => {
                    onEdit(documento);
                    onClose();
                  }}
                >
                  Editar
                </button>
                <button
                  className="docleg-action-btn docleg-action-delete"
                  onClick={() => {
                    onDelete(documento.id);
                    onClose();
                  }}
                >
                  Eliminar
                </button>
                {transitions.map((est) => (
                  <button
                    key={est}
                    className="docleg-action-btn docleg-action-transition"
                    style={{
                      borderColor: ESTADO_COLORS[est],
                      color: ESTADO_COLORS[est],
                    }}
                    onClick={() => {
                      onChangeEstado(documento.id, est);
                      onClose();
                    }}
                  >
                    Mover a {ESTADO_LABELS[est]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default DocumentoLegalDetailPanel;
